import React, { FC } from 'react';

import { EvFlexColumn, EvTabs, EvTitle } from '@evinced-private/ui-common';

import { DASHBOARD_BUSINESS_UNIT_SUB_TABS_SECTION_DAID } from 'data-automation-ids/DashboardAutomationIds';
import { BusinessUnitsTable } from 'src/apps/dashboard/components/business-units-table';
import { DEFAULT_DASHBOARD_TABS_ID } from 'src/apps/dashboard/constants/DashboardTemplate';
import {
	BusinessUnitsTableData,
	DashboardTemplateTabsBottom
} from 'src/apps/dashboard/types/DashboardTemplate.types';
import { QueryState, TabState } from 'src/apps/dashboard/types/TemplateProps';
import { DASHBOARD_LABELS } from 'src/common/labels/dashboard.labels';

import { DashboardTemplateBottomProps } from './DashboardTemplateBottom.types';

import './DashboardTemplateBottom.scss';

interface TabsBlockProps {
	tabState: TabState;
	tabsId: string;
	tabsAriaLabel: string;
}

interface TabsSectionProps extends TabsBlockProps {
	title?: string;
}

interface BusinessUnitsTableSectionProps {
	title?: string;
	query: QueryState<BusinessUnitsTableData>;
	isUniquenessServiceEnabled: boolean;
}

const TabsBlock: FC<TabsBlockProps> = ({ tabState, tabsId, tabsAriaLabel }) => (
	<div className="sub-tabs-container">
		<EvTabs
			id={tabsId}
			tabs={tabState.tabs}
			tabPanels={tabState.tabPanels}
			defaultTab={tabState.activeTabIndex}
			onTabSelected={tabState.onTabSelected}
			ariaLabel={tabsAriaLabel}
			dataAutomationId={tabState.tabsDataAutomationId}
		/>
	</div>
);

const BusinessUnitSubTabsSection: FC<TabsSectionProps> = ({
	title,
	tabState,
	tabsId,
	tabsAriaLabel
}) => (
	<EvFlexColumn
		as="section"
		role="group"
		ariaLabelledby="sub-tabs-heading"
		dataAutomationId={DASHBOARD_BUSINESS_UNIT_SUB_TABS_SECTION_DAID}
	>
		{title && <EvTitle id="sub-tabs-heading" titleText={title} className="sub-tabs-title" />}
		<TabsBlock tabState={tabState} tabsId={tabsId} tabsAriaLabel={tabsAriaLabel} />
	</EvFlexColumn>
);

const DefaultTabsSection: FC<TabsSectionProps> = ({ title, tabState, tabsId, tabsAriaLabel }) => (
	<EvFlexColumn as="section" gap="LARGE">
		{title && (
			<EvTitle id="dashboard-bottom-heading" titleText={title} className="sub-tabs-title" />
		)}
		<TabsBlock tabState={tabState} tabsId={tabsId} tabsAriaLabel={tabsAriaLabel} />
	</EvFlexColumn>
);

const BusinessUnitsTableSection: FC<BusinessUnitsTableSectionProps> = ({
	title,
	query,
	isUniquenessServiceEnabled
}) => (
	<section className="business-units-section">
		<EvFlexColumn gap="XLARGE">
			{title && <EvTitle id="business-units-heading" titleText={title} />}
			<BusinessUnitsTable
				data={query.data?.rows ?? []}
				isLoading={query.isLoading}
				error={query.error}
				total={query.data?.total ?? 0}
				unfilteredTotal={query.data?.unfilteredTotal ?? 0}
				isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			/>
		</EvFlexColumn>
	</section>
);

const TabsBottomSection: FC<{ bottom: DashboardTemplateTabsBottom }> = ({ bottom }) => {
	const { tabState } = bottom;
	const tabsId = tabState.tabsId ?? DEFAULT_DASHBOARD_TABS_ID;
	const tabsAriaLabel =
		tabState.tabsAriaLabel ?? bottom.title ?? DASHBOARD_LABELS.DASHBOARD_TABS_ARIA_LABEL;
	const tabsProps = { tabState, tabsId, tabsAriaLabel };

	if (bottom.sectionVariant === 'businessUnitSubTabs') {
		return <BusinessUnitSubTabsSection title={bottom.title} {...tabsProps} />;
	}

	return <DefaultTabsSection title={bottom.title} {...tabsProps} />;
};

export const DashboardTemplateBottom: FC<DashboardTemplateBottomProps> = ({
	bottom,
	isUniquenessServiceEnabled
}) => {
	if (bottom.kind === 'businessUnitsTable') {
		return (
			<BusinessUnitsTableSection
				title={bottom.title}
				query={bottom.query}
				isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			/>
		);
	}

	return <TabsBottomSection bottom={bottom} />;
};
